import WidgetWrapper from '../WidgetWrapper';
import { DashboardP2Insights } from '../../types/dashboard.types';
import type { EChartsOption } from 'echarts';
import AppChart from '../charts/AppChart';
import { useThemeMode } from '../../hooks/useThemeMode';

type DashboardCohortConversionWidgetProps = {
  insights: DashboardP2Insights | null;
  compareInsights?: DashboardP2Insights | null;
  compareLabel?: string;
  loading?: boolean;
};

export default function DashboardCohortConversionWidget({
  insights,
  compareInsights = null,
  compareLabel = 'Compare',
  loading = false,
}: DashboardCohortConversionWidgetProps) {
  const current = insights?.cohortConversion;
  const compare = compareInsights?.cohortConversion;
  const { isDark } = useThemeMode();
  const hasCompare = Boolean(compare && compare.cohorts.length > 0);

  const cohorts = current?.cohorts ?? [];

  const labelColor = isDark ? '#CBD5E1' : '#475569';
  const axisLine = isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(148, 163, 184, 0.25)';
  const splitLine = isDark ? 'rgba(148, 163, 184, 0.12)' : 'rgba(148, 163, 184, 0.2)';
  const tooltipBackground = isDark ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255,255,255,0.95)';
  const tooltipBorder = isDark ? 'rgba(148, 163, 184, 0.22)' : 'rgba(15, 23, 42, 0.12)';

  const createTrendOption = (
    chartTitle: string,
    chartCohorts: Array<{
      cohortMonth: string;
      signups: number;
      convertedIn30Days: number;
      conversionRatePct: number;
    }>,
  ): EChartsOption => ({
    backgroundColor: 'transparent',
    animationDuration: 700,
    animationDurationUpdate: 700,
    grid: {
      left: 8,
      right: 8,
      top: 72,
      bottom: 8,
      containLabel: true,
    },
    legend: {
      top: 30,
      textStyle: {
        color: labelColor,
        fontSize: 11,
      },
      itemWidth: 12,
      itemHeight: 8,
    },
    title: {
      text: chartTitle,
      left: 8,
      top: 6,
      textStyle: {
        color: labelColor,
        fontSize: 12,
        fontWeight: 600,
      },
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: tooltipBackground,
      borderColor: tooltipBorder,
      borderWidth: 1,
      textStyle: {
        color: labelColor,
        fontSize: 12,
      },
      axisPointer: {
        type: 'line',
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: chartCohorts.map((item) => item.cohortMonth),
      axisLine: {
        lineStyle: {
          color: axisLine,
        },
      },
      axisLabel: {
        color: labelColor,
        fontSize: 11,
      },
    },
    yAxis: {
      type: 'value',
      name: 'Users',
      nameTextStyle: {
        color: labelColor,
        fontSize: 11,
      },
      axisLabel: {
        color: labelColor,
        fontSize: 11,
      },
      splitLine: {
        lineStyle: {
          color: splitLine,
        },
      },
    },
    series: [
      {
        name: 'Signups',
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        data: chartCohorts.map((item) => item.signups),
        lineStyle: { width: 2.5, color: '#2563EB' },
        itemStyle: { color: '#2563EB' },
        areaStyle: { color: 'rgba(37, 99, 235, 0.12)' },
      },
      {
        name: 'Converted in 30d',
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        data: chartCohorts.map((item) => item.convertedIn30Days),
        lineStyle: { width: 2.5, color: '#0D9488' },
        itemStyle: { color: '#0D9488' },
        areaStyle: { color: 'rgba(13, 148, 136, 0.12)' },
      },
    ],
  });

  const currentTrendOption = createTrendOption('Current period', cohorts);
  const compareTrendOption = createTrendOption(compareLabel, compare?.cohorts ?? []);

  return (
    <WidgetWrapper
      title="Donor Activation Trend"
      titleStyle="w-full px-4 py-3 rounded-xl bg-primary-10/80 dark:bg-slate-800/80 text-sm uppercase tracking-[0.18em] font-semibold text-gray-700 dark:text-gray-200"
      className="h-full w-full p-4 md:p-5 bg-gradient-to-br from-indigo-50/80 via-white/75 to-sky-100/75 dark:from-indigo-900/20 dark:via-slate-900/78 dark:to-sky-900/18 border-indigo-200/50 dark:border-indigo-400/15"
    >
      {loading ? (
        <div className="h-64 animate-pulse rounded-2xl border border-white/60 bg-white/60 dark:border-white/10 dark:bg-slate-900/45" />
      ) : (
        <div className="space-y-3 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <MetricCard label="Signups" value={current?.summary.totalSignups ?? 0} compareValue={compare?.summary.totalSignups} compareLabel={compareLabel} />
            <MetricCard label="Converted in 30d" value={current?.summary.convertedIn30Days ?? 0} compareValue={compare?.summary.convertedIn30Days} compareLabel={compareLabel} />
            <MetricCard label="30d conversion" value={`${(current?.summary.conversionRatePct ?? 0).toFixed(2)}%`} compareValue={compare ? `${compare.summary.conversionRatePct.toFixed(2)}%` : undefined} compareLabel={compareLabel} />
          </div>

          <div className={`grid gap-3 w-full ${hasCompare ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1'}`}>
            <div className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 p-2">
              <div className="h-[320px] w-full">
                <AppChart option={currentTrendOption} height="100%" />
              </div>
            </div>

            {hasCompare && (
              <div className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 p-2">
                <div className="h-[320px] w-full">
                  <AppChart option={compareTrendOption} height="100%" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </WidgetWrapper>
  );
}

type MetricCardProps = {
  label: string;
  value: string | number;
  compareValue?: string | number;
  compareLabel: string;
};

function MetricCard({ label, value, compareValue, compareLabel }: MetricCardProps) {
  return (
    <div className="rounded-xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 px-3 py-2">
      <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{value}</p>
      {compareValue !== undefined && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">{compareLabel}: {compareValue}</p>
      )}
    </div>
  );
}
